import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { productsAPI, reviewsAPI } from '../utils/api';
import cartStore from '../utils/cartStore';
import ProductCard from '../components/ProductCard';
import SizeChartModal from '../components/SizeChartModal';
import ReviewSection from '../components/reviews/ReviewSection';
import RatingStars from '../components/reviews/RatingStars';
import SEO, { SITE_URL } from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { toast } from 'sonner';
import { ShoppingCart, Zap, Ruler } from 'lucide-react';

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const generateSlug = (name) => {
  return name.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[-\s]+/g, '-').replace(/^-|-$/g, '');
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { formatPrice } = useCurrency();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [customNote, setCustomNote] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [reviewSummary, setReviewSummary] = useState(null);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const { data } = await productsAPI.getById(id);
        setProduct(data);
        if (data.colors && data.colors.length > 0) {
          setSelectedColor(data.colors[0]);
        }
        const related = await productsAPI.getAll({ category: data.category });
        setRelatedProducts(related.data.filter(p => p.id !== id).slice(0, 4));
        // Load review summary
        try {
          const { data: rs } = await reviewsAPI.getSummary(data.id);
          setReviewSummary(rs);
        } catch (_) {}
      } catch (_) {
        toast.error('Failed to load product');
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
  }, [id]);

  const handleAddToCart = () => {
    cartStore.addToCart(product, quantity, selectedColor, selectedSize || customNote);
    toast.success(t('addedToCart'));
  };

  const handleBuyNow = () => {
    navigate('/checkout', {
      state: {
        buyNowItem: {
          product,
          quantity,
          color: selectedColor,
          size: selectedSize || customNote,
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="loading">
        <p className="font-outfit text-[#8C8577]">Loading...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="not-found">
        <SEO title="Product Not Found" noindex />
        <p className="font-outfit text-[#8C8577]">Product not found</p>
      </div>
    );
  }

  const hasDiscount = product.discount_price && product.discount_price < product.price;
  const displayPrice = hasDiscount ? product.discount_price : product.price;
  const slug = generateSlug(product.name);
  const canonicalUrl = `${SITE_URL}/product/${product.id}/${slug}`;
  const images = product.images && product.images.length > 0 
    ? product.images.map(img => `${process.env.REACT_APP_BACKEND_URL}/api/files/${img}`)
    : ['https://images.unsplash.com/photo-1649793061426-b36b041ced21?w=800'];
  const fullImageUrls = product.images?.length > 0
    ? product.images.map(img => `${SITE_URL}/api/files/${img}`)
    : [];

  // Product JSON-LD Schema
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description || `Shop ${product.name} from Studio Crochet. Premium ethnic wear.`,
    "image": fullImageUrls,
    "brand": { "@type": "Brand", "name": "Studio Crochet" },
    "category": product.category,
    "sku": product.sku || product.id,
    "offers": {
      "@type": "Offer",
      "url": canonicalUrl,
      "priceCurrency": "INR",
      "price": String(displayPrice),
      "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": { "@type": "Organization", "name": "Studio Crochet" },
      "priceValidUntil": "2027-12-31",
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "INR" },
        "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "IN" }
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 7
      }
    }
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Shop", "item": `${SITE_URL}/shop` },
      { "@type": "ListItem", "position": 3, "name": product.category, "item": `${SITE_URL}/shop?category=${product.category.toLowerCase().replace(/\s+/g, '-')}` },
      { "@type": "ListItem", "position": 4, "name": product.name, "item": canonicalUrl }
    ]
  };

  const seoDescription = product.description
    ? product.description.slice(0, 155) + (product.description.length > 155 ? '...' : '')
    : `Buy ${product.name} online at Studio Crochet. ${product.category}. ₹${displayPrice.toLocaleString()}. COD available. Free shipping.`;

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="product-detail-page">
      <SEO
        title={`${product.name} - ${product.category}`}
        description={seoDescription}
        canonical={canonicalUrl}
        image={fullImageUrls[0]}
        type="product"
        schema={[productSchema, breadcrumbSchema]}
      />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8 font-outfit text-sm text-[#8C8577]" aria-label="Breadcrumb" data-testid="breadcrumb">
          <ol className="flex items-center" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" itemProp="item" className="hover:text-[#C29B57]"><span itemProp="name">Home</span></Link>
              <meta itemProp="position" content="1" />
            </li>
            <span className="mx-2">/</span>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/shop" itemProp="item" className="hover:text-[#C29B57]"><span itemProp="name">Shop</span></Link>
              <meta itemProp="position" content="2" />
            </li>
            <span className="mx-2">/</span>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to={`/shop?category=${product.category.toLowerCase().replace(/\s+/g, '-')}`} itemProp="item" className="hover:text-[#C29B57]">
                <span itemProp="name">{product.category}</span>
              </Link>
              <meta itemProp="position" content="3" />
            </li>
            <span className="mx-2">/</span>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span itemProp="name">{product.name}</span>
              <meta itemProp="position" content="4" />
            </li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="aspect-[3/4] overflow-hidden bg-[#F3EFE6] mb-4" data-testid="main-image">
              <img 
                src={images[currentImageIndex]} 
                alt={`${product.name} - ${product.category} by Studio Crochet${currentImageIndex > 0 ? ` - View ${currentImageIndex + 1}` : ''}`}
                className="w-full h-full object-cover"
                loading={currentImageIndex === 0 ? "eager" : "lazy"}
                width="600"
                height="800"
              />
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-2" data-testid="image-thumbnails">
                {images.map((img, i) => (
                  <button 
                    key={img}
                    onClick={() => setCurrentImageIndex(i)}
                    className={`aspect-square overflow-hidden bg-[#F3EFE6] border-2 ${i === currentImageIndex ? 'border-[#C29B57]' : 'border-transparent'}`}
                    data-testid={`thumbnail-${i}`}
                    aria-label={`View image ${i + 1} of ${product.name}`}
                  >
                    <img src={img} alt={`${product.name} - Thumbnail ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <h1 className="font-cormorant text-4xl sm:text-5xl leading-tight mb-4" data-testid="product-title">{product.name}</h1>
            <p className="font-outfit text-sm uppercase tracking-wider text-[#8C8577] mb-2" data-testid="product-category">{product.category}</p>
            
            {/* Rating & Reviews Link */}
            {reviewSummary && reviewSummary.total_reviews > 0 && (
              <a href="#reviews" className="flex items-center gap-2 mb-4 group" data-testid="product-rating-link">
                <RatingStars rating={Math.round(reviewSummary.average_rating)} size="sm" />
                <span className="font-outfit text-sm text-[#8C8577] group-hover:text-[#C29B57] transition-colors">
                  {reviewSummary.average_rating} ({reviewSummary.total_reviews} review{reviewSummary.total_reviews !== 1 ? 's' : ''})
                </span>
              </a>
            )}
            
            <div className="flex items-center gap-4 mb-6">
              <span className="font-outfit text-3xl text-[#1A1A18]" data-testid="product-price">{formatPrice(displayPrice)}</span>
              {hasDiscount && (
                <span className="font-outfit text-xl text-[#8C8577] line-through" data-testid="original-price">{formatPrice(product.price)}</span>
              )}
            </div>

            <p className="font-outfit text-base leading-relaxed mb-8 text-[#4A463F]" data-testid="product-description">{product.description}</p>

            {/* Color Selection */}
            {product.colors && product.colors.length > 0 && (
              <div className="mb-6">
                <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-3">{t('selectColor')}</label>
                <div className="flex gap-2 flex-wrap" data-testid="color-options">
                  {product.colors.map(color => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 border font-outfit text-sm ${selectedColor === color ? 'border-[#C29B57] bg-[#C29B57] text-white' : 'border-[#EBE4D5] text-[#1A1A18]'}`}
                      data-testid={`color-${color}`}
                      aria-label={`Select color ${color}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18]">Select Size</label>
                <button onClick={() => setShowSizeChart(true)} className="flex items-center gap-1 text-[#C29B57] hover:text-[#99733D] font-outfit text-sm" data-testid="size-chart-link">
                  <Ruler className="w-4 h-4" /> Size Chart
                </button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="size-options">
                {SIZES.map(size => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`px-4 py-2 border font-outfit text-sm min-w-[48px] ${selectedSize === size ? 'border-[#C29B57] bg-[#C29B57] text-white' : 'border-[#EBE4D5] text-[#1A1A18] hover:border-[#C29B57]'}`}
                    data-testid={`size-${size}`}
                    aria-label={`Select size ${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Note */}
            <div className="mb-6">
              <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-3">{t('customNote')}</label>
              <input
                type="text"
                value={customNote}
                onChange={(e) => setCustomNote(e.target.value)}
                placeholder="Any custom requirements..."
                className="w-full border-b border-[#EBE4D5] bg-transparent rounded-none px-0 py-2 focus:ring-0 focus:border-[#C29B57] font-outfit text-base placeholder:text-[#8C8577] outline-none"
                data-testid="custom-note-input"
              />
            </div>

            {/* Quantity */}
            <div className="mb-8">
              <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-3">{t('quantity')}</label>
              <div className="flex items-center gap-4">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 border border-[#EBE4D5] flex items-center justify-center" data-testid="decrease-quantity" aria-label="Decrease quantity">-</button>
                <span className="font-outfit text-lg" data-testid="quantity-display">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 border border-[#EBE4D5] flex items-center justify-center" data-testid="increase-quantity" aria-label="Increase quantity">+</button>
              </div>
            </div>

            {/* Stock Status */}
            {product.stock > 0 ? (
              <p className="text-sm text-[#3A5A40] mb-6" data-testid="in-stock">{t('inStock')} ({product.stock} {t('available')})</p>
            ) : (
              <p className="text-sm text-[#9E2A2B] mb-6" data-testid="out-of-stock">{t('outOfStock')}</p>
            )}

            {/* Add to Cart & Buy Now */}
            <div className="flex gap-4">
              <button onClick={handleAddToCart} disabled={product.stock === 0} className="flex-1 bg-[#1A1A18] text-white hover:bg-[#C29B57] transition-colors duration-300 rounded-none px-6 py-4 font-outfit uppercase tracking-wider text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="add-to-cart-button">
                <ShoppingCart className="w-5 h-5" /> {t('addToCart')}
              </button>
              <button onClick={handleBuyNow} disabled={product.stock === 0} className="flex-1 bg-[#C29B57] text-white hover:bg-[#99733D] transition-colors duration-300 rounded-none px-6 py-4 font-outfit uppercase tracking-wider text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="buy-now-button">
                <Zap className="w-5 h-5" /> {t('buyNow')}
              </button>
            </div>
          </div>
        </div>

        <SizeChartModal isOpen={showSizeChart} onClose={() => setShowSizeChart(false)} />

        {/* Reviews Section */}
        <ReviewSection productId={product.id} />

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-24" data-testid="related-products">
            <h2 className="font-cormorant text-2xl sm:text-3xl lg:text-4xl leading-tight mb-12">{t('youMayAlsoLike')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {relatedProducts.map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
